import { getCredentials } from "@connect/credentials-storage";
import { createFramesClient } from "@connect/data/frames/client";
import { log } from "@connect/observability";
import { errorCode, parseErrorBody } from "../../../features/partner-auth";
import type {
  HostedChallenge,
  HostedChallengeParams,
  HostedChallengeResult,
} from "./hosted-challenge.types";
import { runWithTransientRetry, waitForBackoff } from "./retry-backoff";

interface Attempt {
  httpStatus: number;
  data?: { data: HostedChallenge };
  error?: unknown;
}

const NETWORK_FAILURE_STATUS = 0;

const OUTCOMES: Record<string, HostedChallengeResult> = {
  verification_pending: { kind: "pending" },
  no_outstanding_requirements: { kind: "complete" },
  unsupported_country: { kind: "unavailable" },
  conflict: { kind: "constraint" },
};

export async function createHostedChallenge(
  params: HostedChallengeParams,
): Promise<HostedChallengeResult> {
  const { customerToken, clientToken } = await getCredentials();
  if (!customerToken || !clientToken) {
    return failure("missing_credentials", NETWORK_FAILURE_STATUS);
  }

  const client = createFramesClient({
    baseUrl: params.apiBaseUrl,
    headers: {
      ...params.clientHeaders,
      "x-client-token": clientToken,
      Authorization: `Bearer ${customerToken}`,
    },
  });

  const attempt = await runWithTransientRetry(
    () =>
      send(() =>
        client.POST("/platform/v1/identity-verification/challenge", { signal: params.signal }),
      ),
    isTransient,
    params.retryWait ?? waitForBackoff,
    params.signal,
  );

  if (attempt.data) {
    return { kind: "challenge", challenge: attempt.data.data, clientToken };
  }

  const code = errorCode(parseErrorBody(attempt.error));
  const outcome = code ? OUTCOMES[code] : undefined;
  if (outcome) {
    return outcome;
  }

  return failure(code ?? `http_${attempt.httpStatus}`, attempt.httpStatus);
}

async function send(
  call: () => Promise<{ data?: Attempt["data"]; error?: unknown; response: Response }>,
): Promise<Attempt> {
  try {
    const { data, error, response } = await call();
    return { httpStatus: response.status, data, error };
  } catch {
    return { httpStatus: NETWORK_FAILURE_STATUS };
  }
}

function isTransient({ httpStatus }: Attempt): boolean {
  return httpStatus === NETWORK_FAILURE_STATUS || httpStatus === 429 || httpStatus >= 500;
}

function failure(reason: string, httpStatus: number): HostedChallengeResult {
  log.warn("connect.onboarding.hosted_challenge_failed", { reason, httpStatus });

  return { kind: "error", reason };
}
